import { css } from "@emotion/react";
import type { PropsWithChildren } from "react";
import { Suspense, useMemo } from "react";
import { Focusable } from "react-aria";
import { graphql, useLazyLoadQuery } from "react-relay";
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from "react-resizable-panels";
import { useSearchParams } from "react-router";
import invariant from "tiny-invariant";

import {
  Flex,
  Link,
  LinkButton,
  Loading,
  RichTooltip,
  Text,
  TooltipArrow,
  TooltipTrigger,
  View,
} from "@phoenix/components";
import { TraceAgentChatPanel } from "@phoenix/components/agent/TraceAgentChatPanel";
import { compactResizeHandleCSS } from "@phoenix/components/resize";
import { LatencyText } from "@phoenix/components/trace/LatencyText";
import { SpanStatusCodeIcon } from "@phoenix/components/trace/SpanStatusCodeIcon";
import { useSpanStatusCodeColor } from "@phoenix/components/trace/useSpanStatusCodeColor";
import { SELECTED_SPAN_NODE_ID_PARAM } from "@phoenix/constants/searchParams";
import { costFormatter } from "@phoenix/utils/numberFormatUtils";

import { RichTokenBreakdown } from "../../components/RichTokenCostBreakdown";
import type {
  TraceDetailsQuery,
  TraceDetailsQuery$data,
} from "./__generated__/TraceDetailsQuery.graphql";
import { ConnectedTraceTree } from "./ConnectedTraceTree";
import { SpanDetails } from "./SpanDetails";
import { TraceHeaderRootSpanAnnotations } from "./TraceHeaderRootSpanAnnotations";
import { TraceHeaderTraceAnnotations } from "./TraceHeaderTraceAnnotations";

type RootSpan = NonNullable<
  TraceDetailsQuery$data["project"]["trace"]
>["rootSpans"]["edges"][number]["span"];

type CostSummary = NonNullable<
  TraceDetailsQuery$data["project"]["trace"]
>["costSummary"];

export type TraceDetailsProps = {
  traceId: string;
  projectId: string;
};

/**
 * A component that shows the details of a trace (e.g. a collection of spans)
 */
export function TraceDetails(props: TraceDetailsProps) {
  const { traceId, projectId } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const data = useLazyLoadQuery<TraceDetailsQuery>(
    graphql`
      query TraceDetailsQuery($traceId: ID!, $id: ID!) {
        project: node(id: $id) {
          ... on Project {
            trace(traceId: $traceId) {
              id
              projectSessionId
              ...ConnectedTraceTree
              rootSpans: spans(
                first: 1
                rootSpansOnly: true
                orphanSpanAsRootSpan: true
              ) {
                edges {
                  span: node {
                    statusCode
                    id
                    spanId
                    parentId
                  }
                }
              }
              latencyMs
              costSummary {
                prompt {
                  cost
                }
                completion {
                  cost
                }
                total {
                  cost
                }
              }
            }
          }
        }
      }
    `,
    { traceId: traceId as string, id: projectId as string },
    {
      fetchPolicy: "store-and-network",
    }
  );
  invariant(data.project.trace, "Trace is required to view the trace details");
  const traceLatencyMs =
    data.project.trace?.latencyMs != null ? data.project.trace.latencyMs : null;
  const costSummary = data?.project?.trace?.costSummary;
  const rootSpans: RootSpan[] = useMemo(() => {
    const gqlSpans = data.project.trace?.rootSpans.edges || [];
    return gqlSpans.map((node) => node.span);
  }, [data]);
  const urlSpanNodeId = searchParams.get(SELECTED_SPAN_NODE_ID_PARAM);
  invariant(rootSpans.length > 0, "At least one root must be resolvable");
  const rootSpan = rootSpans[0];
  const selectedSpanNodeId = urlSpanNodeId ?? rootSpan.id;


  return (
    <main
      css={css`
        flex: 1 1 auto;
        overflow: hidden;
        display: flex;
        flex-direction: column;
      `}
    >
      <TraceHeader
        projectId={projectId}
        rootSpan={rootSpan}
        traceNodeId={data.project.trace?.id ?? null}
        latencyMs={traceLatencyMs}
        costSummary={costSummary}
        sessionId={data.project.trace?.projectSessionId}
      />
      
      {searchParams.get('evaluation') && (
        <div css={css`
          margin: 12px 16px 4px 16px;
          background: ${searchParams.get('severity') === 'Critical' ? 'rgba(239, 68, 68, 0.06)' : 'rgba(245, 158, 11, 0.06)'};
          border: ${searchParams.get('severity') === 'Critical' ? '1px solid rgba(239, 68, 68, 0.25)' : '1px solid rgba(245, 158, 11, 0.25)'};
          border-radius: 12px;
          padding: 16px 20px;
          display: flex;
          gap: 24px;
          color: var(--global-text-color-900);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
        `}>
          {/* Left panel: Issue Details */}
          <div css={css`flex: 1; display: flex; flex-direction: column; gap: 8px; border-right: 1px solid var(--global-border-color-default); padding-right: 24px;`}>
            <div css={css`display: flex; align-items: center; gap: 8px;`}>
              <span css={css`
                font-size: 8px; font-weight: 800; text-transform: uppercase;
                color: ${searchParams.get('severity') === 'Critical' ? '#ef4444' : '#f59e0b'};
                background: ${searchParams.get('severity') === 'Critical' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(245, 158, 11, 0.1)'};
                padding: 3px 8px; border-radius: 8px; border: 1px solid ${searchParams.get('severity') === 'Critical' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(245, 158, 11, 0.2)'};
              `}>
                {searchParams.get('severity') || 'High'}
              </span>
              <h4 css={css`margin: 0; font-size: 13px; font-weight: 800;`}>
                Evaluation Diagnostics: <span css={css`color: var(--global-text-color-600);`}>{searchParams.get('evaluation')}</span> &gt; <span css={css`color: #ef4444;`}>{searchParams.get('issue')}</span>
              </h4>
            </div>
            
            <p css={css`margin: 4px 0 0 0; font-size: 12px; color: var(--global-text-color-700); line-height: 1.5;`}>
              <strong>Root Cause:</strong> {searchParams.get('rootCause')} {searchParams.get('affectedSpan') && <>in span <code>{searchParams.get('affectedSpan')}</code></>}
            </p>
            
            <div css={css`display: flex; gap: 16px; margin-top: 8px; font-size: 11px;`}>
              <div>
                <span css={css`color: var(--global-text-color-400); font-weight: 700; text-transform: uppercase; font-size: 9px;`}>Expected</span>
                <div css={css`font-weight: 700; color: #10b981; margin-top: 2px;`}>{searchParams.get('expected') || 'Pass'}</div>
              </div>
              <div>
                <span css={css`color: var(--global-text-color-400); font-weight: 700; text-transform: uppercase; font-size: 9px;`}>Actual</span>
                <div css={css`font-weight: 700; color: #ef4444; margin-top: 2px;`}>{searchParams.get('actual') || 'Fail'}</div>
              </div>
            </div>
          </div>
          
          {/* Right panel: Recommendations */}
          <div css={css`flex: 1; display: flex; flex-direction: column; gap: 8px; justify-content: center;`}>
            <h5 css={css`margin: 0; font-size: 11px; font-weight: 800; color: var(--global-text-color-500); text-transform: uppercase; letter-spacing: 0.5px;`}>
              💡 Recommended Action
            </h5>
            <p css={css`margin: 0; font-size: 12px; color: var(--global-text-color-800); line-height: 1.5;`}>
              {searchParams.get('recommendation') || 'No custom recommendation provided. Inspect model and system configuration.'}
            </p>
            <div css={css`margin-top: 4px; font-size: 11px;`}>
              <Link 
                to="/dashboard"
                css={css`color: #3b82f6; font-weight: 700; &:hover { text-decoration: underline; }`}
              >
                ← Back to Evaluations Dashboard
              </Link>
            </div>
          </div>
        </div>
      )}

      <PanelGroup
        direction="horizontal"
        css={css`
          flex: 1 1 auto;
          overflow: hidden;
        `}
      >
        <Panel id="trace-tree" defaultSize={30} minSize={5}>
          <ScrollingPanelContent>
            <ConnectedTraceTree
              trace={data.project.trace}
              selectedSpanNodeId={selectedSpanNodeId}
              onSpanClick={(span) => {
                setSearchParams(
                  (searchParams) => {
                    searchParams.set(SELECTED_SPAN_NODE_ID_PARAM, span.id);
                    return searchParams;
                  },
                  { replace: true }
                );
              }}
            />
          </ScrollingPanelContent>
        </Panel>
        <PanelResizeHandle css={compactResizeHandleCSS} />
        <Panel id="span-details">
          <ScrollingTabsWrapper>
            {selectedSpanNodeId ? (
              <Suspense fallback={<Loading />}>
                <SpanDetails spanNodeId={selectedSpanNodeId} />
              </Suspense>
            ) : null}
          </ScrollingTabsWrapper>
        </Panel>
        <TraceAgentChatPanel />
      </PanelGroup>
    </main>
  );
}

function TraceHeader({
  rootSpan,
  traceNodeId,
  latencyMs,
  costSummary,
  sessionId,
  projectId,
}: {
  rootSpan: RootSpan | null;
  traceNodeId: string | null;
  latencyMs: number | null;
  costSummary?: CostSummary | null;
  sessionId?: string | null;
  projectId: string;
}) {
  const { statusCode } = rootSpan ?? {
    statusCode: "UNSET",
  };
  const statusColor = useSpanStatusCodeColor(statusCode);
  return (
    <View
      paddingTop="size-100"
      paddingBottom="size-150"
      paddingX="size-200"
      borderBottomWidth="thin"
      borderBottomColor="default"
    >
      <Flex
        direction="row"
        gap="size-400"
        alignItems="flex-start"
        css={css`
          box-sizing: content-box;
          max-height: 180px;
          min-width: 0;
          max-width: 100%;
        `}
      >
        <Flex direction="column">
          <Text elementType="h3" size="S" color="text-700">
            Trace Status
          </Text>
          <Text size="XL">
            <Flex direction="row" gap="size-50" alignItems="center">
              <SpanStatusCodeIcon statusCode={statusCode} />
              <Text size="L" color={statusColor}>
                {statusCode}
              </Text>
            </Flex>
          </Text>
        </Flex>
        <Flex direction="column">
          <Text elementType="h3" size="S" color="text-700">
            Total Cost
          </Text>
          <TooltipTrigger delay={0}>
            <Focusable>
              <Text size="L" role="button">
                {costFormatter(costSummary?.total?.cost ?? 0)}
              </Text>
            </Focusable>
            <RichTooltip placement="bottom">
              <TooltipArrow />
              <View width="size-3600">
                <RichTokenBreakdown
                  valueLabel="cost"
                  totalValue={costSummary?.total?.cost ?? 0}
                  formatter={costFormatter}
                  segments={[
                    {
                      name: "Prompt",
                      value: costSummary?.prompt?.cost ?? 0,
                      color: "rgba(254, 119, 99, 1)",
                    },
                    {
                      name: "Completion",
                      value: costSummary?.completion?.cost ?? 0,
                      color: "rgba(98, 104, 239, 1)",
                    },
                  ]}
                />
              </View>
            </RichTooltip>
          </TooltipTrigger>
        </Flex>
        <Flex direction="column">
          <Text elementType="h3" size="S" color="text-700">
            Latency
          </Text>
          <Text size="XL">
            {typeof latencyMs === "number" ? (
              <LatencyText latencyMs={latencyMs} size="L" />
            ) : (
              "--"
            )}
          </Text>
        </Flex>
        {traceNodeId ? (
          <TraceHeaderTraceAnnotations traceId={traceNodeId} />
        ) : null}
        {rootSpan ? (
          <TraceHeaderRootSpanAnnotations
            spanId={rootSpan.id}
            excludeMirrorAnnotations
          />
        ) : null}
        {sessionId && (
          <span
            css={css`
              margin-left: auto;
            `}
          >
            <LinkButton
              size="S"
              variant="primary"
              to={`/projects/${projectId}/sessions/${sessionId}`}
            >
              View Session
            </LinkButton>
          </span>
        )}
      </Flex>
    </View>
  );
}

function ScrollingTabsWrapper({ children }: PropsWithChildren) {
  return (
    <div
      data-testid="scrolling-tabs-wrapper"
      css={css`
        height: 100%;
        overflow: hidden;
        .tabs {
          height: 100%;
          overflow: hidden;
          .tabs__extra {
            width: 100%;
            padding-right: var(--global-dimension-size-200);
            padding-bottom: var(--global-dimension-size-50);
          }
          .tabs__pane-container {
            min-height: 100%;
            height: 100%;
            overflow-y: auto;
            div[role="tabpanel"] {
              height: 100%;
            }
          }
        }
      `}
    >
      {children}
    </div>
  );
}

function ScrollingPanelContent({ children }: PropsWithChildren) {
  return (
    <div
      data-testid="scrolling-panel-content"
      css={css`
        height: 100%;
        overflow-y: auto;
      `}
    >
      {children}
    </div>
  );
}
